'use client';

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { createWorkerClient } from '../../../../lib/entry.browser.js';
import { wagerQueryKeys } from '../../../../lib/entry.js';
import { useQuery } from '../../../../tanstack/entry.js';
import { bestBetRowsOptions } from '../../../queryOptions/entry.js';
import { useGetDrawResult } from '../../draw/hooks/useGetDrawResult.js';
import { useEngine } from '../../engine/entry.js';
import { defaultWorker } from '../defaultWorker.js';
import { useWager } from './useWager.js';
const composeResultRow = results => results.map(({
  outcome
}) => outcome);
function useForecastResultForWager(wagerId, filter) {
  const $ = _c(8);
  const wager = useWager(wagerId);
  const {
    product,
    drawNumber
  } = wager.data.coupon;
  let t0;
  if ($[0] !== drawNumber || $[1] !== product) {
    t0 = {
      product,
      drawNumber
    };
    $[0] = drawNumber;
    $[1] = product;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const forecast = useGetDrawResult(t0);
  let t1;
  if ($[3] !== forecast.data.data || $[4] !== forecast.data.type) {
    t1 = forecast.data.type === "NONE" ? [] : forecast.data.data.drawResults;
    $[3] = forecast.data.data;
    $[4] = forecast.data.type;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const forecastResult = t1;
  let t2;
  if ($[6] !== forecastResult) {
    t2 = composeResultRow(forecastResult);
    $[6] = forecastResult;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const srow = t2;
  return filter ?? srow;
}
let workerApi = defaultWorker;
function handleBeforeUnload() {
  workerApi.terminate();
}
if (typeof window !== 'undefined') {
  // ----- IF MORE THREADS IS DESIRED -----
  // The creation of the worker can be moved inside the react hook inside a useMemo and teared down in an empty useEffect.
  // However, instantiating the worker in the hook will create a new worker thread upon every unique hook call,
  // ie. if two components calls the hook there will be two workers.
  workerApi = createWorkerClient(
  // Worker and URL must always coexist, see:
  // https://github.com/vercel/next.js/issues/31009
  new Worker(new URL('../bestBetRowsWorker.js', import.meta.url), {
    type: 'module'
  }));
  window.addEventListener('beforeunload', handleBeforeUnload);
}
/**
 * To be documented.
 *
 * @param wagerId Wager Id to calculate best bet rows for.
 * @param filter Result filter row
 * @param maxRows Optional cap on the total number of SROWs returned across all win divisions.
 * When provided, the worker only returns at most `maxRows` rows (in division order).
 * @returns Best bet rows for wager
 */
export function useBestBetRows(wagerId, filter, maxRows) {
  const $ = _c(11);
  const resultSrow = useForecastResultForWager(wagerId, filter);
  const definition = useEngine();
  const queryClient = useQueryClient();
  const previousResultSrowRef = useRef(resultSrow);
  let t0;
  let t1;
  if ($[0] !== maxRows || $[1] !== queryClient || $[2] !== resultSrow || $[3] !== wagerId) {
    t0 = () => {
      const currentResultSrowKey = resultSrow.join(",");
      if (previousResultSrowRef.current.join(",") !== currentResultSrowKey) {
        queryClient.invalidateQueries({
          queryKey: wagerQueryKeys.wagerBestBetRows(wagerId, resultSrow, maxRows)
        });
        previousResultSrowRef.current = resultSrow;
      }
    };
    t1 = [resultSrow, wagerId, maxRows, queryClient];
    $[0] = maxRows;
    $[1] = queryClient;
    $[2] = resultSrow;
    $[3] = wagerId;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
  let t2;
  if ($[6] !== definition || $[7] !== maxRows || $[8] !== resultSrow || $[9] !== wagerId) {
    t2 = bestBetRowsOptions({
      wagerId,
      resultSrow,
      maxRows
    }, {
      post: workerApi.post,
      definition
    });
    $[6] = definition;
    $[7] = maxRows;
    $[8] = resultSrow;
    $[9] = wagerId;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return useQuery(t2);
}